import { getAltitudeColorCSS } from '../utils/colors.js';
import { getAircraftTypeName } from '../utils/aircraftType.js';
import { countryFlagForHex } from '../utils/countryFlags.js';
import { FindMyPlane } from './findMyPlane.js';
import {
  getAirlineName,
  fetchRoute,
  fetchAircraftPhoto,
  fetchTypePhoto,
  estimateFlightDuration,
  formatDuration,
  haversineKm
} from '../data/enrichment.js';
import { fetchDossier, peekDossier } from '../data/aiDossier.js';

/**
 * Flight info panel — compact by default so the map stays visible.
 *
 * Wischbare Seiten (Apple-Style Punkte):
 *   0 = Flugdetails, 1 = Weitere Infos (KI/Websuche), 2 = Find my Plane.
 *
 * Live-Werte aktualisieren bei jedem Poll; Route + Foto werden einmal pro Auswahl
 * geladen (in enrichment.js gecacht, gegen Races via enrichSeq geschützt). Das
 * KI-Dossier lädt per Klick, ist an den Hex gebunden und dreifach gecacht — bei
 * erneuter Auswahl erscheint es sofort. Fehlt ein echtes Airframe-Foto, wird ein
 * repräsentatives Typ-Bild klar als „Demo“ markiert angezeigt.
 */
export class InfoPanel {
  constructor() {
    this.panel = document.getElementById('infoPanel');
    this.elements = {
      callsign: document.getElementById('infoCallsign'),
      airline: document.getElementById('infoAirline'),
      sumSpotlight: document.getElementById('sumSpotlight'),
      sumAlt: document.getElementById('sumAlt'),
      sumSpeed: document.getElementById('sumSpeed'),
      sumType: document.getElementById('sumType'),
      sumRoute: document.getElementById('sumRoute'),
      sumLoading: document.getElementById('sumLoading'),
      routeSkeleton: document.getElementById('routeSkeleton'),
      photoWrap: document.getElementById('infoPhotoWrap'),
      photo: document.getElementById('infoPhoto'),
      photoDemo: document.getElementById('infoPhotoDemo'),
      photoCredit: document.getElementById('infoPhotoCredit'),
      route: document.getElementById('infoRoute'),
      routeFromCode: document.getElementById('routeFromCode'),
      routeFromName: document.getElementById('routeFromName'),
      routeToCode: document.getElementById('routeToCode'),
      routeToName: document.getElementById('routeToName'),
      duration: document.getElementById('infoDuration'),
      routeEta: document.getElementById('routeEta'),
      arrival: document.getElementById('infoArrival'),
      remaining: document.getElementById('infoRemaining'),
      altitude: document.getElementById('infoAltitude'),
      speed: document.getElementById('infoSpeed'),
      heading: document.getElementById('infoHeading'),
      vertical: document.getElementById('infoVertical'),
      phase: document.getElementById('infoPhase'),
      origin: document.getElementById('infoOrigin')
    };

    // Welcher Flug gerade angereichert ist/wird (verhindert Doppel-Fetch
    // bei den 5s-Poll-Updates und veraltete Antworten nach Auswahlwechsel)
    this.enrichedFlightId = null;
    this.enrichSeq = 0;
    this.route = null; // aktuelle Route des ausgewählten Fluges (für ETA)

    // Wird beim Schließen (X, Swipe-runter) aufgerufen, damit die Auswahl
    // in Cesium ebenfalls aufgehoben wird — sonst zeigt das nächste
    // Poll-Update das Panel wieder an (selectedEntity blieb gesetzt).
    this.onClose = null;

    // Wischbare Seiten (0 = Flugdetails, 1 = Weitere Infos, 2 = Find my Plane)
    this.page = 0;
    this.pagesEl = this.panel.querySelector('.info-pages');
    this.pages = [
      this.panel.querySelector('.info-page-details'),
      this.panel.querySelector('.info-page-ai'),
      this.panel.querySelector('.info-page-find')
    ];
    this.dots = Array.from(this.panel.querySelectorAll('.info-dot'));
    this.summaryEl = document.querySelector('.info-summary');
    this.detailsEl = document.getElementById('infoDetails');
    this.tiltControl = document.getElementById('tiltControl');
    this.findMyPlane = new FindMyPlane();

    // KI-Dossier-Seite (Seite 1)
    this.aiIntro = document.getElementById('aiIntro');
    this.aiFetchBtn = document.getElementById('aiFetchBtn');
    this.aiHint = document.getElementById('aiHint');
    this.aiSkeleton = document.getElementById('aiSkeleton');
    this.aiResult = document.getElementById('aiResult');
    this.dossierSeq = 0;
    this.dossierRenderedHex = null;
    this.currentFlight = null;
    this._shownHex = null;
    this.spotlight = null; // via setSpotlightState() gesetzt

    this.aiFetchBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      this._onDossierFetch();
    });

    // Close button
    document.getElementById('closeInfo').addEventListener('click', (e) => {
      e.stopPropagation();
      this.hide();
    });

    // Details auf-/zuklappen
    this.expandBtn = document.getElementById('expandInfo');
    this.expandBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      this.setCollapsed(!this.panel.classList.contains('collapsed'));
    });

    // Seiten-Punkte: direkt zur Seite springen
    this.dots.forEach((dot) => {
      dot.addEventListener('click', (e) => {
        e.stopPropagation();
        this.setPage(Number(dot.dataset.page));
      });
    });

    // Klick irgendwo auf das Panel (Header, Flugnummer, Chips …) klappt die
    // Details auf/zu — bequemer als den kleinen Pfeil zu treffen.
    this.panel.addEventListener('click', (e) => {
      // Nach einem Swipe unterdrücken, damit der Tap-Toggle nicht gegenwirkt
      if (this.suppressClick) {
        this.suppressClick = false;
        return;
      }
      // Interaktive Elemente behalten ihr eigenes Verhalten
      if (e.target.closest('a, button, input, .info-dot')) return;
      // Nur auf der Detailseite auf-/zuklappen
      if (this.page !== 0) return;
      // Klicks in bereits geöffnete Details nicht als Toggle werten (Textauswahl)
      if (!this.panel.classList.contains('collapsed') && e.target.closest('#infoDetails')) return;
      this.setCollapsed(!this.panel.classList.contains('collapsed'));
    });
    this.summaryEl.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.setCollapsed(!this.panel.classList.contains('collapsed'));
      }
    });

    // Swipe: hoch = ausklappen, runter = einklappen/schließen,
    // links/rechts = Seite wechseln
    this.initSwipeGestures();

    // Höhe des aktiven Seiteninhalts spiegeln (sanfte Höhen-Animation) und
    // Panelhöhe für Zeitleiste/Buttons bereitstellen
    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(() => this.updatePagesHeight());
      this.pages.forEach((p) => ro.observe(p));
      ro.observe(this.panel);
    }
  }

  /**
   * Gemeinsamen Spotlight-Zustand anbinden — versorgt den Badge in der
   * Kurzansicht. Aktualisiert live, wenn der Screener später ein Urteil liefert.
   */
  setSpotlightState(state) {
    this.spotlight = state;
    state.on('change', ({ hex }) => {
      if (this._shownHex && hex === this._shownHex.toLowerCase()) {
        this._updateSpotlightChip(this._shownHex);
      }
    });
  }

  setCollapsed(collapsed) {
    this.panel.classList.toggle('collapsed', collapsed);
    this.expandBtn.setAttribute('aria-expanded', String(!collapsed));
    this.expandBtn.title = collapsed ? 'Details anzeigen' : 'Details ausblenden';
    this.updateExpandedFlag();
    this.updatePagesHeight();
  }

  /**
   * Zur Seite index wechseln (0 = Flugdetails, 1 = Weitere Infos, 2 = Find my Plane).
   */
  setPage(index) {
    index = Math.max(0, Math.min(2, index | 0));
    this.page = index;
    this.panel.dataset.page = String(index);
    this.dots.forEach((dot, i) => {
      dot.classList.toggle('is-active', i === index);
      dot.setAttribute('aria-selected', String(i === index));
    });
    this.pages.forEach((p, i) => p.setAttribute('aria-hidden', String(i !== index)));
    if (index === 1) this.activateDossier();
    if (index === 2) this.findMyPlane.activate();
    this.updateExpandedFlag();
    this.updatePagesHeight();
  }

  /**
   * Neigungsregler rechts ausblenden, sobald ein Flugzeug ausgewählt ist —
   * schon in der Kurzansicht, nicht erst beim Aufklappen der Details.
   */
  updateExpandedFlag() {
    const expanded = !this.panel.classList.contains('hidden');
    document.body.classList.toggle('info-expanded', expanded);
  }

  /**
   * Höhe des Seiten-Containers an den aktiven Seiteninhalt anpassen — sorgt
   * für eine sanfte Höhen-Animation. Die KI-Seite (1) wird bei ~62vh gedeckelt
   * und scrollt dann intern (overflow-y), damit das Panel nie über den Rand ragt.
   */
  updatePagesHeight() {
    if (this.panel.classList.contains('hidden')) return;
    let h;
    if (this.page === 2) {
      h = this.pages[2].scrollHeight;
    } else if (this.page === 1) {
      const cap = Math.round(window.innerHeight * 0.62);
      h = Math.min(this.pages[1].scrollHeight, cap);
    } else if (this.panel.classList.contains('collapsed')) {
      h = this.summaryEl.offsetHeight;
    } else {
      h = this.pages[0].scrollHeight;
    }
    this.pagesEl.style.height = `${h}px`;
    this.updateLayoutOffset();
  }

  /**
   * Panelhöhe als CSS-Variable spiegeln, damit die Buttons unten (Zeitleiste,
   * Ansicht zurücksetzen) über das Panel wandern statt verdeckt zu werden.
   * 0 wenn ausgeblendet.
   */
  updateLayoutOffset() {
    const visible = !this.panel.classList.contains('hidden');
    const h = visible ? this.panel.offsetHeight : 0;
    document.documentElement.style.setProperty('--info-panel-height', `${h}px`);
    // Ohne Panel darf die Zeitleiste höher sitzen (nicht am unteren Rand kleben)
    document.documentElement.classList.toggle('has-info-panel', visible);
  }

  /**
   * Touch-Swipe auf dem Panel:
   *   hoch   → Details ausklappen (nur auf der Detailseite)
   *   runter → eine Seite zurück, sonst einklappen bzw. Panel schließen
   *   links  → nächste Seite, rechts → vorherige Seite
   */
  initSwipeGestures() {
    let startX = null;
    let startY = null;
    const H_THRESHOLD = 45;
    const V_THRESHOLD = 45;

    this.panel.addEventListener('touchstart', (e) => {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
    }, { passive: true });

    this.panel.addEventListener('touchend', (e) => {
      if (startX === null) return;
      const dx = e.changedTouches[0].clientX - startX;
      const dy = e.changedTouches[0].clientY - startY;
      startX = null;
      startY = null;

      if (Math.abs(dx) > Math.abs(dy)) {
        // Horizontaler Swipe → Seite wechseln
        if (Math.abs(dx) < H_THRESHOLD) return;
        this.suppressClick = true;
        this.setPage(this.page + (dx < 0 ? 1 : -1));
        return;
      }

      // Vertikaler Swipe
      if (Math.abs(dy) < V_THRESHOLD) return;
      this.suppressClick = true;
      if (dy > 0) {
        // nach unten
        if (this.page > 0) {
          this.setPage(this.page - 1);
        } else if (!this.panel.classList.contains('collapsed')) {
          this.setCollapsed(true);
        } else {
          this.hide();
        }
      } else {
        // nach oben → ausklappen (nur auf der Detailseite)
        if (this.page === 0) this.setCollapsed(false);
      }
    }, { passive: true });
  }

  /**
   * Show panel with flight data
   * @param {Object} flight - Flight data object
   */
  show(flight) {
    this.currentFlight = flight;
    this._shownHex = flight.id;

    // Neuer Flug ausgewählt → kompakt starten (mehr Karte sichtbar)
    const isNewSelection = flight.id !== this.enrichedFlightId;
    if (isNewSelection) {
      this.setPage(0);
      this.setCollapsed(true);
    }

    // Find-my-Plane mit aktueller Position versorgen
    this.findMyPlane.update(flight);

    // Callsign with color based on altitude
    const flag = countryFlagForHex(flight.id);
    this.elements.callsign.textContent =
      (flight.callsign || flight.id) + (flag ? ` ${flag}` : '');
    this.elements.callsign.style.color = getAltitudeColorCSS(flight.position.altitude);

    // Airline (aus dem ICAO-Prefix des Callsigns)
    this.elements.airline.textContent = getAirlineName(flight.callsign) || '';

    // Spotlight-Badge (falls der Screener den Flug als besonders markiert hat)
    this._updateSpotlightChip(flight.id);

    // Altitude in meters and feet
    const altMeters = Math.round(flight.position.altitude);
    const altFeet = Math.round(flight.position.altitude * 3.28084);
    this.elements.altitude.textContent = `${altMeters} m (${altFeet} ft)`;

    // Speed in km/h and knots
    const speedKmh = Math.round(flight.velocity * 3.6);
    const speedKnots = Math.round(flight.velocity * 1.94384);
    this.elements.speed.textContent = `${speedKmh} km/h (${speedKnots} kt)`;

    // Kompakte Chip-Zeile
    this.elements.sumAlt.textContent = `${altMeters.toLocaleString('de-DE')} m`;
    this.elements.sumSpeed.textContent = `${speedKmh} km/h`;
    const typeName = getAircraftTypeName(flight.aircraftType);
    this.elements.sumType.textContent = typeName || '';
    this.elements.sumType.classList.toggle('hidden', !typeName);

    // Heading
    const heading = Math.round(flight.position.heading);
    const direction = this.getCardinalDirection(heading);
    this.elements.heading.textContent = `${heading}° ${direction}`;

    // Vertical rate
    const verticalRate = Math.round(flight.verticalRate);
    let verticalText = `${verticalRate} m/s`;
    if (verticalRate > 0) {
      verticalText = `+${verticalText} ↑`;
      this.elements.vertical.style.color = '#0faf5f';
    } else if (verticalRate < 0) {
      verticalText = `${verticalText} ↓`;
      this.elements.vertical.style.color = '#f57c00';
    } else {
      this.elements.vertical.style.color = 'var(--color-text-dim)';
    }
    this.elements.vertical.textContent = verticalText;

    // Phase
    const phaseLabels = {
      ground: 'Am Boden',
      climbing: 'Steigflug',
      descending: 'Sinkflug',
      cruising: 'Reiseflug'
    };
    this.elements.phase.textContent = phaseLabels[flight.phase] || flight.phase;

    // Aircraft type + registration
    const aircraftInfo = [typeName || flight.aircraftType, flight.registration]
      .filter(Boolean)
      .join(' · ');
    this.elements.origin.textContent = aircraftInfo || flight.originCountry || 'Unbekannt';

    // Route + Foto + Dossier-Reset nur beim Wechsel des ausgewählten Flugzeugs
    if (isNewSelection) {
      this.enrichedFlightId = flight.id;
      this.enrich(flight);
      this._resetDossierView();
      this._loadCachedDossier(flight);
    }

    // ETA bei jedem Poll-Update neu berechnen (Position/Speed ändern sich)
    this.updateEta(flight);

    // Show panel
    this.panel.classList.remove('hidden');
    this.updateExpandedFlag();
    this.updatePagesHeight();
  }

  /**
   * Ankunftszeit aus Restdistanz zum Zielflughafen und aktueller
   * Geschwindigkeit schätzen (+ pauschal 8 min für Anflug/Landung).
   */
  updateEta(flight) {
    const destination = this.route?.destination;
    if (typeof destination?.lat !== 'number' || flight.onGround) {
      this.elements.routeEta.classList.add('hidden');
      return;
    }

    const remainingKm = haversineKm(
      { lat: flight.position.lat, lon: flight.position.lon },
      destination
    );

    // Bei sehr niedriger Geschwindigkeit (Warteschleife, schlechte Daten)
    // keine unsinnigen ETAs anzeigen
    const speedMs = flight.velocity;
    if (!speedMs || speedMs < 40) {
      this.elements.routeEta.classList.add('hidden');
      return;
    }

    const etaMinutes = (remainingKm * 1000) / speedMs / 60 + 8;
    const arrival = new Date(Date.now() + etaMinutes * 60 * 1000);
    const clock = arrival.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });

    this.elements.arrival.textContent = `~${clock} Uhr`;
    this.elements.remaining.textContent =
      `noch ${formatDuration(Math.round(etaMinutes))} · ${Math.round(remainingKm)} km`;
    this.elements.routeEta.classList.remove('hidden');
  }

  /**
   * Fetch route + photo asynchronously; only apply results if the
   * selection has not changed in the meantime.
   */
  enrich(flight) {
    const seq = ++this.enrichSeq;

    // Reset enrichment areas + Lade-Skeletons zeigen
    this.route = null;
    this.elements.route.classList.add('hidden');
    this.elements.routeEta.classList.add('hidden');
    this.elements.photoWrap.classList.add('hidden');
    this.elements.photoDemo.classList.add('hidden');
    this.elements.photoWrap.classList.remove('info-photo-demo-on');
    this.elements.photo.onerror = null; // veralteten Fallback-Handler lösen
    this.elements.photo.removeAttribute('src');
    this.elements.sumRoute.classList.add('hidden');
    this.elements.sumLoading.classList.remove('hidden');
    this.elements.routeSkeleton.classList.remove('hidden');

    fetchRoute(flight.callsign, flight.position.lat, flight.position.lon)
      .then((route) => {
        if (seq !== this.enrichSeq) return;

        // Lade-Skeletons ausblenden — auch wenn keine Route bekannt ist
        this.elements.sumLoading.classList.add('hidden');
        this.elements.routeSkeleton.classList.add('hidden');
        if (!route) return;

        const { origin, destination } = route;
        this.route = route;

        const fromCode = origin.iata || origin.icao || '?';
        const toCode = destination.iata || destination.icao || '?';
        this.elements.routeFromCode.textContent = fromCode;
        this.elements.routeFromName.textContent = origin.location || origin.name || '';
        this.elements.routeToCode.textContent = toCode;
        this.elements.routeToName.textContent = destination.location || destination.name || '';

        // Kompakter Routen-Chip in der Zusammenfassung
        this.elements.sumRoute.textContent = `${fromCode} → ${toCode}`;
        this.elements.sumRoute.classList.remove('hidden');

        const est = estimateFlightDuration(origin, destination);
        this.elements.duration.textContent = est
          ? `Reisezeit ~${formatDuration(est.minutes)} · ${est.distanceKm} km`
          : '';

        this.elements.route.classList.remove('hidden');
        this.updateEta(flight);
      });

    fetchAircraftPhoto(flight.id).then((photo) => {
      if (seq !== this.enrichSeq) return;
      if (photo) {
        // Echtes Airframe-Foto. Falls die URL nicht LÄDT (404/blockiert), fällt
        // der onError-Handler auf ein Typ-Demobild zurück.
        this._showPhoto(photo, false, () => this._loadDemoPhoto(flight, seq));
        return;
      }
      // Kein echtes Foto dieses Airframes bekannt → direkt Typ-Demobild
      this._loadDemoPhoto(flight, seq);
    });
  }

  /**
   * Repräsentatives Bild des TYPS (gleiches Modell) aus dem Internet holen und
   * klar als „Demo“ (Rand + Badge) anzeigen — wenn kein echtes bzw. kein
   * ladefähiges Airframe-Foto vorliegt.
   */
  _loadDemoPhoto(flight, seq) {
    if (seq !== this.enrichSeq) return;
    const term = getAircraftTypeName(flight.aircraftType) || flight.aircraftType;
    if (!term) {
      this.elements.photoWrap.classList.add('hidden');
      this.updatePagesHeight();
      return;
    }
    fetchTypePhoto(term).then((demo) => {
      if (seq !== this.enrichSeq) return;
      if (!demo) {
        this.elements.photoWrap.classList.add('hidden');
        this.updatePagesHeight();
        return;
      }
      this._showPhoto(demo, true, null);
    });
  }

  /**
   * Foto (echt oder Typ-Demo) im gemeinsamen Foto-Bereich anzeigen.
   * @param {{src, link, photographer}} photo
   * @param {boolean} isDemo  Demo-Rand + Badge einblenden
   * @param {?Function} onError  Fallback, wenn die Bild-URL nicht lädt
   *                             (null = kein weiterer Fallback → Bereich ausblenden)
   */
  _showPhoto(photo, isDemo, onError) {
    const img = this.elements.photo;
    img.onerror = () => {
      img.onerror = null;
      if (onError) {
        onError(); // echtes Foto kaputt → Typ-Demobild versuchen
      } else {
        // auch das Demobild lädt nicht → Foto-Bereich sauber ausblenden
        this.elements.photoWrap.classList.add('hidden');
        this.updatePagesHeight();
      }
    };
    img.src = photo.src;
    this.elements.photoCredit.textContent = photo.photographer ? `© ${photo.photographer}` : '';
    this.elements.photoCredit.href = photo.link || '#';
    this.elements.photoDemo.classList.toggle('hidden', !isDemo);
    this.elements.photoWrap.classList.toggle('info-photo-demo-on', isDemo);
    this.elements.photoWrap.classList.remove('hidden');
    this.updatePagesHeight();
  }

  /** Spotlight-Badge in der Kurzansicht setzen/ausblenden. */
  _updateSpotlightChip(hex) {
    const chip = this.elements.sumSpotlight;
    if (!chip) return;
    const verdict = this.spotlight ? this.spotlight.get(hex) : null;
    if (verdict && verdict.interesting && verdict.label) {
      chip.textContent = verdict.label;
      chip.classList.remove('hidden');
    } else {
      chip.classList.add('hidden');
    }
  }

  // ---- KI-Dossier (Seite 1) --------------------------------------------

  /** Seite 1 wurde sichtbar — Höhe aktualisieren, ggf. Cache nachladen. */
  activateDossier() {
    if (!this.dossierRenderedHex && this.currentFlight) {
      this._loadCachedDossier(this.currentFlight);
    }
    this.updatePagesHeight();
  }

  /** Zurück auf den Ausgangszustand (Intro + „Mehr erfahren“-Button). */
  _resetDossierView() {
    this.dossierRenderedHex = null;
    this.dossierSeq++; // laufende/veraltete Antworten verwerfen
    this.aiIntro.classList.remove('hidden');
    this.aiSkeleton.classList.add('hidden');
    this.aiResult.classList.add('hidden');
    this.aiResult.replaceChildren();
    this.aiHint.textContent = '';
    this.aiFetchBtn.disabled = false;
  }

  /** Bereits gecachtes Dossier sofort zeigen (ohne Netzaufruf, ohne Klick). */
  _loadCachedDossier(flight) {
    if (!flight) return;
    const seq = this.dossierSeq;
    const hex = flight.id;
    peekDossier(hex).then((dossier) => {
      if (seq !== this.dossierSeq || !dossier) return; // Auswahl gewechselt / kein Cache
      this._renderDossier(dossier);
      this.dossierRenderedHex = hex;
    });
  }

  /** „Mehr erfahren“ geklickt → Dossier vom Backend holen. */
  _onDossierFetch() {
    const flight = this.currentFlight;
    if (!flight) return;
    const seq = ++this.dossierSeq;

    this.aiIntro.classList.add('hidden');
    this.aiResult.classList.add('hidden');
    this.aiSkeleton.classList.remove('hidden');
    this.aiHint.textContent = '';
    this.updatePagesHeight();

    fetchDossier(flight)
      .then((dossier) => {
        if (seq !== this.dossierSeq) return;
        this.aiSkeleton.classList.add('hidden');
        if (dossier) {
          this._renderDossier(dossier);
          this.dossierRenderedHex = flight.id;
        } else {
          this._dossierError('Keine Infos gefunden.');
        }
      })
      .catch(() => {
        if (seq !== this.dossierSeq) return;
        this.aiSkeleton.classList.add('hidden');
        this._dossierError('Gerade nicht erreichbar – später erneut versuchen.');
      });
  }

  _dossierError(msg) {
    this.aiIntro.classList.remove('hidden');
    this.aiHint.textContent = msg;
    this.updatePagesHeight();
  }

  /** Dossier-Envelope als sichere DOM-Knoten rendern (kein innerHTML). */
  _renderDossier(dossier) {
    const el = this.aiResult;
    el.replaceChildren();

    const summary = dossier?.summary || dossier?.text;
    if (!dossier || (!dossier.title && !summary && !Array.isArray(dossier.highlights))) {
      this._dossierError('Keine Infos gefunden.');
      return;
    }

    const frag = document.createDocumentFragment();

    if (dossier.title) {
      const h = document.createElement('h4');
      h.className = 'ai-title';
      h.textContent = dossier.title;
      frag.appendChild(h);
    }

    const badges = Array.isArray(dossier.badges) ? dossier.badges : [];
    const rarity = Number(dossier.rarity) || 0;
    if (badges.length || rarity) {
      const row = document.createElement('div');
      row.className = 'ai-badges';
      badges.slice(0, 4).forEach((b) => {
        const chip = document.createElement('span');
        chip.className = 'ai-badge';
        chip.textContent = String(b);
        row.appendChild(chip);
      });
      if (rarity) {
        const n = Math.max(1, Math.min(5, rarity));
        const meter = document.createElement('span');
        meter.className = 'ai-rarity';
        meter.title = `Seltenheit ${n}/5`;
        meter.textContent = '★'.repeat(n) + '☆'.repeat(5 - n);
        row.appendChild(meter);
      }
      frag.appendChild(row);
    }

    if (summary) {
      const p = document.createElement('p');
      p.className = 'ai-summary';
      p.textContent = summary;
      frag.appendChild(p);
    }

    if (Array.isArray(dossier.highlights) && dossier.highlights.length) {
      const ul = document.createElement('ul');
      ul.className = 'ai-highlights';
      dossier.highlights.slice(0, 8).forEach((item) => {
        const li = document.createElement('li');
        li.textContent = String(item);
        ul.appendChild(li);
      });
      frag.appendChild(ul);
    }

    const sources = Array.isArray(dossier.sources) ? dossier.sources : [];
    const validSources = sources.filter((s) => s && typeof s.url === 'string' && /^https?:\/\//i.test(s.url));
    if (validSources.length) {
      const box = document.createElement('div');
      box.className = 'ai-sources';
      const lbl = document.createElement('span');
      lbl.className = 'ai-sources-label';
      lbl.textContent = 'Quellen';
      box.appendChild(lbl);
      validSources.slice(0, 6).forEach((s) => {
        const a = document.createElement('a');
        a.href = s.url;
        a.target = '_blank';
        a.rel = 'noopener';
        a.className = 'ai-source';
        a.textContent = s.title || hostOf(s.url);
        box.appendChild(a);
      });
      frag.appendChild(box);
    }

    el.replaceChildren(frag);
    this.aiIntro.classList.add('hidden');
    this.aiSkeleton.classList.add('hidden');
    el.classList.remove('hidden');

    // sanfte Einblendung (Animation bei jedem Render neu starten)
    el.classList.remove('ai-reveal');
    void el.offsetWidth;
    el.classList.add('ai-reveal');

    this.updatePagesHeight();
  }

  /**
   * Hide the panel
   */
  hide() {
    this.panel.classList.add('hidden');
    this.enrichedFlightId = null;
    this.currentFlight = null;
    this._shownHex = null;
    this.updateExpandedFlag();
    this.updateLayoutOffset();
    // Auswahl in Cesium aufheben, damit das nächste Poll-Update das Panel
    // nicht erneut einblendet. Idempotent: wird selectedEntity bereits
    // geleert (Klick auf leere Karte), löst dies kein weiteres Event aus.
    if (this.onClose) this.onClose();
  }

  /**
   * Get cardinal direction from heading
   * @param {number} heading - Heading in degrees
   * @returns {string}
   */
  getCardinalDirection(heading) {
    const directions = ['N', 'NO', 'O', 'SO', 'S', 'SW', 'W', 'NW'];
    const index = Math.round(heading / 45) % 8;
    return directions[index];
  }
}

/** Host-Teil einer URL als Fallback-Beschriftung einer Quelle. */
function hostOf(url) {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}
